import { useEffect, useState, useRef, useCallback } from 'react';
import { EditorData } from '../../../utils/editorUtils';
import { useMapData } from './MapDataContext';
import { useAppState } from '../../../AppContext';

// Global preload lock to prevent duplicate preloads from multiple hook instances
let globalPreloadLock = false;

/**
 * Hook for managing cached layer data with automatic preloading
 * 
 * Features:
 * - Preloads all layer data on first layer access
 * - Uses cache for instant switching between layers
 * - Handles global changes by refreshing all data
 */
export const useCachedLayerData = (layerId: number | undefined) => {
  const { getLayerData, hasGlobalChanges, clearGlobalChangesFlag, cacheVersion } = useMapData();
  const { state } = useAppState();
  const [data, setData] = useState<EditorData<"map"> | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  
  // Use refs to track preload state without causing re-renders
  const hasPreloadedRef = useRef(false);
  const isPreloadingRef = useRef(false);
  const lastGlobalChangesRef = useRef(hasGlobalChanges);

  /**
   * Stable reference to getLayerData to prevent unnecessary re-renders
   */
  const stableGetLayerData = useCallback((id: number, forceRefresh?: boolean) => {
    return getLayerData(id, forceRefresh);
  }, [getLayerData]);

  useEffect(() => {
    if (!layerId) {
      setData(null);
      setLoading(false);
      return;
    }

    // Reset preload flag when global changes occur
    if (hasGlobalChanges !== lastGlobalChangesRef.current) {
      if (hasGlobalChanges) {
        hasPreloadedRef.current = false;
        globalPreloadLock = false; // Reset global lock on global changes
      }
      lastGlobalChangesRef.current = hasGlobalChanges;
    }

    const fetchData = async () => {
      setLoading(true);
      
      try {
        // Only preload when there are actual global changes, or on first access
        const needsPreload = !hasPreloadedRef.current && !isPreloadingRef.current && !globalPreloadLock && 
                            state.map?.layerGroups;
        
        if (needsPreload) {
          const shouldForceRefresh = hasGlobalChanges;
          
          // Set both local and global locks
          isPreloadingRef.current = true;
          globalPreloadLock = true;
          
          const layerIds = state.map.layerGroups.map(layer => layer.id);
          const preloadPromises = layerIds.map(async (id: number) => {
            try {
              return await stableGetLayerData(id, shouldForceRefresh);
            } catch (error) {
              console.warn(`Failed to preload data for layer ${id}:`, error);
              return null;
            }
          });
          
          await Promise.all(preloadPromises);
          
          hasPreloadedRef.current = true;
          isPreloadingRef.current = false;
          globalPreloadLock = false;
          
          // Clear global changes flag after successful preload
          if (hasGlobalChanges) {
            clearGlobalChangesFlag();
          }
        }
        
        // Get specific layer data (from cache if available)
        const layerData = await stableGetLayerData(layerId, false);
        setData(layerData);
        
      } catch (error) {
        setData(null);
        isPreloadingRef.current = false;
        globalPreloadLock = false; // Clear global lock on error
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [layerId, hasGlobalChanges, stableGetLayerData, clearGlobalChangesFlag, state.map?.layerGroups, cacheVersion]);

  return { data, loading };
};
